
import { Component, Vue } from "vue-property-decorator";
import { CallReceptionState, SettingsApi } from "@/api";
import { AxiosError } from "axios";
import { getDefaultApiConfig, parseAxiosError } from "@/utils";
import {
  applyConnectedDeviceSelection,
  areAvailableDevicesLoading,
  areConnectedDevicesUpdating,
  beginCallReceptionUpdate,
  deviceFor,
  deviceKey,
  endCallReceptionUpdate,
  getAvailableDevices,
  getCallReceptionState,
  getSelectedDeviceKeys,
  isCallReceptionStateLoading,
  refreshCallReceptionState,
  refreshConnectedDevices,
  setCallReceptionState,
  subscribeCallReception,
  unsubscribeCallReception,
} from "@/store/callReception";

@Component({
  name: "CallReceptionStatus",
})
export default class CallReceptionStatus extends Vue {
  States = CallReceptionState;

  get loading(): boolean {
    return isCallReceptionStateLoading();
  }

  get state(): CallReceptionState | null {
    return getCallReceptionState();
  }

  set state(value: CallReceptionState | null) {
    setCallReceptionState(value);
  }

  toggling = false;
  toggleResetKey = 0;

  settingsApi = new SettingsApi(getDefaultApiConfig());

  get selectedKeys(): string[] {
    return getSelectedDeviceKeys();
  }

  get loadingDevices(): boolean {
    return areAvailableDevicesLoading();
  }

  get updatingDevices(): boolean {
    return areConnectedDevicesUpdating();
  }

  get deviceItems(): { key: string; label: string }[] {
    return getAvailableDevices().map((d) => ({
      key: deviceKey(d),
      label: d.title ? `${d.title} (${d.name})` : d.name ?? "",
    }));
  }

  get isReceiving(): boolean {
    return this.state === CallReceptionState.Receiving;
  }

  async onDevicesChange(newKeys: string[]) {
    try {
      await applyConnectedDeviceSelection(newKeys);
    } catch (error) {
      this.notifyActionError(
        error,
        "Změnu připojených zařízení se nepodařilo provést."
      );
    }
  }

  mounted() {
    subscribeCallReception();
  }

  beforeDestroy() {
    unsubscribeCallReception();
  }

  reload() {
    refreshCallReceptionState();
  }

  async onToggle(receiving: boolean) {
    if (this.toggling) {
      return;
    }
    this.toggling = true;
    beginCallReceptionUpdate();
    try {
      let response;
      if (receiving) {
        const device = deviceFor(this.selectedKeys[0]);
        if (!device) {
          return;
        }
        response = await this.settingsApi.setCallReception({
          receiving: true,
          extension: device.name,
          model: device.model,
        });
      } else {
        response = await this.settingsApi.setCallReception({
          receiving: false,
        });
      }
      this.state = response.data.state ?? CallReceptionState.Unavailable;
      if (
        this.state === CallReceptionState.InvalidToken ||
        this.state === CallReceptionState.Unavailable
      ) {
        this.$sideAlert(
          this.state === CallReceptionState.InvalidToken
            ? "Daktela token je neplatný. Vygenerujte si v Daktele nový."
            : "Příjem hovorů se nepodařilo zapnout. Zkontrolujte, že jste přihlášen v Daktele.",
          "error"
        );
      }
      await refreshConnectedDevices();
    } catch (error) {
      this.notifyActionError(error, "Přepnutí příjmu hovorů se nepodařilo.");
      this.toggleResetKey += 1;
      await refreshCallReceptionState(true);
    } finally {
      this.toggling = false;
      endCallReceptionUpdate();
    }
  }

  notifyActionError(error: unknown, fallback: string) {
    const messages = parseAxiosError(error as AxiosError);
    this.$sideAlert(
      messages.length > 0 ? messages.join(" ") : fallback,
      "error"
    );
  }
}
